// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import axios from 'axios';

/**
 * Perform a health check on a Stargate device
 * @param {string} ip - IP address of the Stargate
 * @param {string} apiKey - API key for authentication
 * @param {number} maxAttempts - Maximum number of attempts (default 3)
 * @param {number} timeout - Timeout per attempt in ms (default 3000)
 * @returns {Promise<{healthy: boolean, error?: Error}>}
 */
async function checkStargateHealth(ip, apiKey, maxAttempts = 3, timeout = 30000) {
  if (!ip || !apiKey) {
    return {
      healthy: false,
      error: {
        message: 'Missing IP address or API key',
        type: 'configuration',
      },
    };
  }

  const token = localStorage.getItem('token');
  let lastError = null;
  let errorType = 'unknown';
  let errorMessage = 'Unable to connect to the Stargate';

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const source = axios.CancelToken.source();

    try {
      // Use the new health check endpoint that doesn't require authentication
      const response = await axios.get('/api/health/stargate', {
        params: {
          ip,
          api_key: apiKey,
          force_protocol: 'http', // Always use HTTP
        },
        timeout,
        cancelToken: source.token,
        validateStatus: function (status) {
          // Don't throw for any status, we'll handle it ourselves
          return true;
        },
      });

      // Check if we got a response from our health check endpoint
      if (response.status === 200) {
        // The new endpoint returns the health status in the response body
        const data = response.data;
        if (data.healthy) {
          return { healthy: true };
        } else {
          errorType = data.errorType || 'unknown';
          errorMessage = data.error || 'Health check failed';

          // Special handling for different error types
          if (data.errorType === 'authentication') {
            // Don't retry on authentication errors
            lastError = { message: errorMessage, type: errorType };
            break;
          } else if (data.errorType === 'no_endpoints' && attempt === 1) {
            // If no HTTPS endpoints work, try HTTP as fallback
            // Continue to HTTP fallback logic below
          } else {
            // For other errors, store and continue retrying
            lastError = { message: errorMessage, type: errorType };
          }
        }
      } else {
        // Unexpected status from our endpoint
        throw new Error(`Health check endpoint returned status: ${response.status}`);
      }
    } catch (error) {
      // Handle specific error cases with user-friendly messages
      if (error.response) {
        switch (error.response.status) {
          case 401:
            errorType = 'authentication';
            errorMessage =
              'Authentication failed. The API key may be incorrect or the Stargate rejected the connection.';
            break;
          case 403:
            errorType = 'authorization';
            errorMessage = 'Access denied. You may not have permission to access this Stargate.';
            break;
          case 404:
            errorType = 'not_found';
            errorMessage =
              'The Stargate endpoint could not be found. The device may be running outdated firmware.';
            break;
          case 500:
          case 502:
          case 503:
          case 504:
            errorType = 'server_error';
            errorMessage =
              'The Stargate encountered an error. It may be experiencing technical difficulties.';
            break;
          default:
            errorType = 'http_error';
            errorMessage = `Failed to connect to the Stargate (Error ${error.response.status}).`;
        }
        lastError = {
          ...error,
          message: errorMessage,
          type: errorType,
          status: error.response.status,
        };
      } else if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
        errorType = 'timeout';
        errorMessage = 'Connection timed out. The Stargate may be offline or unreachable.';
        lastError = { ...error, message: errorMessage, type: errorType };
      } else if (error.code === 'ERR_NETWORK' || error.code === 'ENOTFOUND') {
        errorType = 'network';
        errorMessage =
          'Network error. Unable to reach the Stargate. Please check the IP address and network connectivity.';
        lastError = { ...error, message: errorMessage, type: errorType };
      } else {
        lastError = {
          ...error,
          message: error.message || errorMessage,
          type: errorType,
        };
      }

      // Don't retry HTTP if we got a 401 - the API key is wrong
      if (error.response && error.response.status === 401) {
        // Make sure we set the error details
        lastError = {
          ...error,
          message: errorMessage,
          type: errorType,
          status: 401,
        };
        break; // Exit the retry loop immediately
      }

      // If it's a connection error or no_endpoints error, try HTTP fallback
      if (
        attempt === 1 &&
        (error.code === 'ECONNABORTED' ||
          error.code === 'ERR_NETWORK' ||
          !error.response ||
          errorType === 'no_endpoints')
      ) {
        try {
          const httpResponse = await axios.get('/api/health/stargate', {
            params: {
              ip,
              api_key: apiKey,
              force_protocol: 'http', // Fallback to HTTP
            },
            timeout,
            cancelToken: source.token,
            validateStatus: function (status) {
              return true; // Don't throw for any status
            },
          });

          // Check if we actually got a successful response
          if (httpResponse.status === 200) {
            const data = httpResponse.data;
            if (data.healthy) {
              return { healthy: true };
            } else {
              errorType = data.errorType || 'unknown';
              errorMessage = data.error || 'Health check failed';
              lastError = { message: errorMessage, type: errorType };
            }
          } else {
            // Unexpected status code
            lastError = {
              message: `Unexpected status code: ${httpResponse.status}`,
              type: 'http_error',
            };
          }
        } catch (httpError) {
          // Process HTTP error the same way
          if (httpError.response && httpError.response.status === 401) {
            errorType = 'authentication';
            errorMessage =
              'Authentication failed. The API key may be incorrect or the Stargate rejected the connection.';
          }
          lastError = {
            ...httpError,
            message: errorMessage,
            type: errorType,
          };
        }
      }

      // Log attempt failure (but not the full error to avoid console spam)
      if (attempt < maxAttempts) {
        // Retry silently
      }
    }
  }

  // All attempts failed
  return { healthy: false, error: lastError };
}

/**
 * Create a health check context that can be used by multiple components
 */
export class StargateHealthChecker {
  constructor(ip, apiKey) {
    this.ip = ip;
    this.apiKey = apiKey;
    this.healthStatus = null;
    this.checkPromise = null;
    this.lastCheckTime = 0;
    this.checkInterval = 30000; // Base check interval
    this.retryCount = 0;
    this.maxRetryCount = 5; // Maximum retry count before giving up
    this.maxCheckInterval = 300000; // Maximum 5 minutes between checks
  }

  async getHealthStatus() {
    const now = Date.now();

    // Calculate dynamic check interval with exponential backoff
    const dynamicInterval = this.getDynamicInterval();

    // If we have a recent health check result, return it
    if (this.healthStatus && now - this.lastCheckTime < dynamicInterval) {
      return this.healthStatus;
    }

    // If a check is already in progress, wait for it
    if (this.checkPromise) {
      return this.checkPromise;
    }

    // Perform a new health check with fewer retries for periodic checks
    const timeout = 30000; // Use 30 second timeout for cellular networks
    const maxAttempts = this.retryCount > 0 ? 1 : 2; // Fewer attempts after initial check

    this.checkPromise = checkStargateHealth(this.ip, this.apiKey, maxAttempts, timeout)
      .then((result) => {
        this.healthStatus = result;
        this.lastCheckTime = now;
        this.checkPromise = null;

        // Reset retry count on successful check
        if (result.healthy) {
          this.retryCount = 0;
        } else {
          // Increment retry count for failed checks (with cap)
          this.retryCount = Math.min(this.retryCount + 1, this.maxRetryCount);
        }

        return result;
      })
      .catch((error) => {
        // If checkStargateHealth throws, convert to a failed health status
        const errorResult = {
          healthy: false,
          error: {
            message: 'An unexpected error occurred while checking Stargate connectivity',
            type: 'unknown',
            originalError: error,
          },
        };
        this.healthStatus = errorResult;
        this.lastCheckTime = now;
        this.checkPromise = null;

        // Increment retry count for errors
        this.retryCount = Math.min(this.retryCount + 1, this.maxRetryCount);

        return errorResult;
      });

    return this.checkPromise;
  }

  getDynamicInterval() {
    // Implement exponential backoff
    // Base interval * (2 ^ retryCount), capped at max interval
    const backoffInterval = this.checkInterval * Math.pow(2, this.retryCount);
    return Math.min(backoffInterval, this.maxCheckInterval);
  }

  clearCache() {
    this.healthStatus = null;
    this.lastCheckTime = 0;
    this.checkPromise = null;
    this.retryCount = 0; // Reset retry count when manually clearing cache
  }
}
